import React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import { STATUS_COLORS, STATUS_LABELS, TYPE_LABELS } from "../constants";

export default function DashboardLatestLeaves({ latestLeaves, leaveStats }) {
    return (
        <Card
            sx={{
                borderRadius: 4,
                height: "100%",
                boxShadow: "0 22px 44px rgba(15, 23, 42, 0.12)",
                border: "1px solid rgba(148, 163, 184, 0.28)",
                background:
                    "linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(245, 248, 255, 0.92) 50%, rgba(255, 255, 255, 0.94) 100%)",
                backdropFilter: "blur(12px)",
                overflow: "hidden",
                position: "relative",
                transition:
                    "transform 200ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 200ms cubic-bezier(0.4, 0, 0.2, 1), border-color 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 30px 68px rgba(15, 23, 42, 0.18)",
                    borderColor: "rgba(148, 163, 184, 0.45)",
                },
            }}
        >
            {/* Accent top bar */}
            <Box
                sx={{
                    height: 6,
                    background:
                        "linear-gradient(90deg, #FF9015, rgba(255, 255, 255, 0.85), #FFC100)",
                }}
            />

            {/* Radial ambient glow */}
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background: `
                        radial-gradient(circle at 12% 12%, rgba(255, 144, 21, 0.08), transparent 50%),
                        radial-gradient(circle at 88% 12%, rgba(15, 23, 42, 0.04), transparent 45%),
                        radial-gradient(circle at 50% 100%, rgba(15, 23, 42, 0.03), transparent 55%)
                    `,
                    pointerEvents: "none",
                }}
            />

            <CardContent sx={{ p: { xs: 2, md: 3 }, position: "relative" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            letterSpacing: "-0.01em",
                            color: "rgba(15, 23, 42, 0.9)",
                        }}
                    >
                        Dernières demandes
                    </Typography>
                    <Chip
                        label={`${leaveStats.total} total`}
                        size="small"
                        variant="outlined"
                        sx={{
                            borderColor: "rgba(255, 144, 21, 0.35)",
                            color: "#FF9015",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            letterSpacing: "0.02em",
                            backdropFilter: "blur(6px)",
                            background: "rgba(255, 144, 21, 0.06)",
                            transition:
                                "background 200ms cubic-bezier(0.4, 0, 0.2, 1), border-color 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": {
                                background: "rgba(255, 144, 21, 0.12)",
                                borderColor: "rgba(255, 144, 21, 0.55)",
                            },
                        }}
                    />
                </Stack>
                <Divider
                    sx={{
                        my: 2,
                        borderColor: "rgba(148, 163, 184, 0.22)",
                    }}
                />
                <Stack spacing={1}>
                    {latestLeaves.length ? (
                        latestLeaves.map((leave, index) => (
                            <Box
                                key={`${leave.id ?? "leave"}-${leave.from ?? "from"}-${leave.to ?? "to"}-${index}`}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 2,
                                    flexWrap: "wrap",
                                    px: 2,
                                    py: 1.5,
                                    borderRadius: 2.5,
                                    transition:
                                        "background 200ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                                    "&:hover": {
                                        background: "rgba(255, 144, 21, 0.04)",
                                        boxShadow:
                                            "0 2px 8px rgba(255, 144, 21, 0.08)",
                                    },
                                }}
                            >
                                <Box>
                                    <Typography
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: "0.9rem",
                                            color: "rgba(15, 23, 42, 0.88)",
                                            letterSpacing: "-0.005em",
                                        }}
                                    >
                                        {leave.prenom} {leave.nom}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: "rgba(100, 116, 139, 0.85)",
                                            fontSize: "0.8rem",
                                            fontWeight: 400,
                                            mt: 0.25,
                                        }}
                                    >
                                        {leave.from} - {leave.to}
                                    </Typography>
                                </Box>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Chip
                                        label={TYPE_LABELS[leave.type] || leave.type}
                                        size="small"
                                        color="default"
                                        variant="outlined"
                                        sx={{
                                            fontSize: "0.72rem",
                                            fontWeight: 500,
                                            borderColor: "rgba(148, 163, 184, 0.35)",
                                            transition:
                                                "background 200ms cubic-bezier(0.4, 0, 0.2, 1), border-color 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                                            "&:hover": {
                                                background: "rgba(148, 163, 184, 0.08)",
                                                borderColor: "rgba(148, 163, 184, 0.55)",
                                            },
                                        }}
                                    />
                                    <Chip
                                        label={STATUS_LABELS[leave.status] || leave.status}
                                        size="small"
                                        color={STATUS_COLORS[leave.status] || "default"}
                                        sx={{
                                            fontSize: "0.72rem",
                                            fontWeight: 600,
                                            transition:
                                                "transform 200ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                                            "&:hover": {
                                                transform: "scale(1.04)",
                                            },
                                        }}
                                    />
                                </Stack>
                            </Box>
                        ))
                    ) : (
                        <Typography
                            variant="body2"
                            sx={{
                                color: "rgba(100, 116, 139, 0.7)",
                                fontStyle: "italic",
                                py: 2,
                                textAlign: "center",
                            }}
                        >
                            Aucune demande récente.
                        </Typography>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}
